export const PARTNER_SESSION_KEY = "oh.partner.session";

/** Canonical partner keys aligned with OneHub partnerAppCatalogueService. */
const APP_TYPE_TO_KEY = {
  JAHEZ: "jahez",
  KEETA: "keeta",
  NINJA: "ninja",
  THE_CHEFZ: "thechefz",
  CHEFZ: "thechefz",
  FOODICS: "foodics",
  TABSENSE: "tabsense",
  TRACK_HUB: "trackhub",
  CHAT_AGENT: "orderagent",
  DIGITAL_CATALOG: "digitalcatalog",
};

const KEY_FROM_TEXT = [
  ["jahez", /jahez/i],
  ["keeta", /keeta/i],
  ["ninja", /ninja/i],
  ["thechefz", /chefz/i],
  ["foodics", /foodics/i],
  ["tabsense", /tabsense|tab\s*sense/i],
  ["trackhub", /trackhub|track\s*hub/i],
  ["orderagent", /order\s*agent|chat[_\s-]?agent/i],
  ["digitalcatalog", /digital\s*catalog/i],
  ["noon", /noons?\b/i],
];

const CHANNEL_DISPLAY = {
  jahez: "Jahez",
  keeta: "Keeta",
  ninja: "Ninja",
  thechefz: "The Chefz",
  foodics: "Foodics",
  tabsense: "TabSense",
  trackhub: "TrackHub",
  orderagent: "Order Agent",
  digitalcatalog: "Digital Catalog",
  noon: "Noon",
};

/** Stable EN label for a partner channel key (avoids mis-matched catalogue names). */
export function channelDisplayName(itemOrKey, fallback = "") {
  if (itemOrKey && typeof itemOrKey === "object") {
    const key = String(itemOrKey.channelKey || "").toLowerCase();
    if (CHANNEL_DISPLAY[key]) return CHANNEL_DISPLAY[key];
    return itemOrKey.channelName || itemOrKey.channelKey || fallback;
  }
  const key = String(itemOrKey || "").toLowerCase();
  return CHANNEL_DISPLAY[key] || fallback || String(itemOrKey || "");
}

function keyFromAppType(appType) {
  const type = String(appType || "").trim().toUpperCase();
  return APP_TYPE_TO_KEY[type] || null;
}

function keyFromText(value) {
  const text = String(value || "").trim();
  if (!text) return null;
  const lower = text.toLowerCase().replace(/[\s-]+/g, "");
  if (Object.values(APP_TYPE_TO_KEY).includes(lower)) return lower;
  const fromType = keyFromAppType(text);
  if (fromType) return fromType;
  const match = KEY_FROM_TEXT.find(([, pattern]) => pattern.test(text));
  return match ? match[0] : null;
}

export function channelKey(channel) {
  if (!channel) return null;
  if (typeof channel === "string") return keyFromText(channel);
  return (
    keyFromText(channel.key)
    || keyFromText(channel.id)
    || keyFromAppType(channel.app_type || channel.appType)
    || keyFromText(channel.name)
    || keyFromText(`${channel.id || ""} ${channel.name || ""}`)
  );
}

export function normalizeSessionItems(session) {
  if (!session || typeof session !== "object") return [];
  if (Array.isArray(session.items) && session.items.length) {
    return session.items.map((row) => ({
      channel: row.channel || row.app || null,
      location: row.location || row.branch || null,
      quantity: Number(row.quantity || 1) || 1,
      channel_key: row.channel_key || channelKey(row.channel || row.app),
      branch_ref: row.branch_ref || row.location?.branch_ref || row.location?.ref || null,
      branch_name: row.branch_name
        || (typeof row.location?.name === "string" ? row.location.name : row.location?.name?.en)
        || "",
    })).filter((row) => row.channel || row.channel_key);
  }
  const channels = Array.isArray(session.channels) && session.channels.length
    ? session.channels
    : (session.channel ? [session.channel] : []);
  const locations = Array.isArray(session.locations) && session.locations.length
    ? session.locations
    : (session.location ? [session.location] : [null]);
  const out = [];
  for (const channel of channels) {
    for (const location of locations) {
      out.push({
        channel,
        location,
        quantity: 1,
        channel_key: channelKey(channel),
        branch_ref: location?.branch_ref || location?.ref || location?.id || null,
        branch_name: typeof location?.name === "string" ? location.name : (location?.name?.en || ""),
      });
    }
  }
  return out;
}

export function readPartnerSession() {
  try {
    const raw = sessionStorage.getItem(PARTNER_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writePartnerSession(session) {
  if (!session) {
    sessionStorage.removeItem(PARTNER_SESSION_KEY);
    window.dispatchEvent(new Event("partner-session"));
    return;
  }
  sessionStorage.setItem(PARTNER_SESSION_KEY, JSON.stringify(session));
  window.dispatchEvent(new Event("partner-session"));
}

export function clearPartnerSession() {
  sessionStorage.removeItem(PARTNER_SESSION_KEY);
  window.dispatchEvent(new Event("partner-session"));
}

export function signedInAccountId() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return String(user?.id || user?._id || "");
  } catch {
    return "";
  }
}

export function matchAvailableApp(apps, channel) {
  const wantedKey = channelKey(channel);
  const channelId = channel?.id != null ? String(channel.id) : "";
  return (apps || []).find((app) => {
    if (channelId && String(app._id || app.id) === channelId) return true;
    const appKey = keyFromAppType(app?.appType) || keyFromText(app?.appType) || keyFromText(app?.name);
    if (wantedKey && appKey && wantedKey === appKey) return true;
    return false;
  }) || null;
}

export function subscriptionPath(app, coupon) {
  const params = new URLSearchParams();
  const id = app?._id || app?.id;
  if (id) params.set("stage", String(id));
  if (coupon) params.set("coupon", String(coupon));
  const query = params.toString();
  return query ? `/subscription?${query}` : "/subscription";
}

export function connectPathForItem(item) {
  const id = item?.appId;
  if (!id) return "/apps-management";
  const type = String(item?.appType || "").toUpperCase();
  if (type === "TRACK_HUB") return `/trackhub-integration/${id}`;
  if (String(item?.connectionMode || "") === "addon_toggle" || type === "CHAT_AGENT" || type === "DIGITAL_CATALOG") {
    return `/addon-integration/${id}`;
  }
  return `/add-app/${id}`;
}

export function partnerConnectReturnPath(sessionId) {
  return `/partner-sessions/${sessionId}?step=connect`;
}

export function buildCartFromPlan(plan, mode, globalCode) {
  if (mode === "global" && globalCode) {
    return [{ planCode: globalCode, quantity: 1 }];
  }
  const items = plan?.suggestedCart?.items || [];
  return items.map((row) => ({
    planCode: row.planCode,
    quantity: Number(row.quantity || 1) || 1,
  })).filter((row) => row.planCode);
}

export function itemKey(item, index = 0) {
  return `${item?.channelKey || item?.appId || "app"}:${item?.branchId || item?.branchRef || index}`;
}

/** Shape used by resolveIntegrationLogo / apps-management local marks. */
export function partnerItemLogoApp(item) {
  if (!item || typeof item !== "object") return null;
  // Prefer channelKey / appType over display name so a bad catalogue match
  // (e.g. Noons labeled JAHEZ) cannot steal the wrong local mark.
  const key = item.channelKey || "";
  const type = item.appType || key;
  return {
    appType: type,
    name: key || item.channelName || "",
    application: key || type || "",
    key,
    logo: item.logo || "",
  };
}
